import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStore } from 'rdx/modules/app/slice';
import type { RootState } from 'index';
import { GenericActionPayload } from 'types/redux-types';
import Resource from 'lib/jsonApi/Resource';
import { SystemDocumentsAttributes } from 'types/systemDocuments';
import { UnwrappedResourceList } from 'types/json-api-types';

const initialState = {
  privacyPolicy: new Resource<SystemDocumentsAttributes>(),
  EULA: new Resource<SystemDocumentsAttributes>(),
  termsOfUse: new Resource<SystemDocumentsAttributes>(),
  releaseNotes: [] as UnwrappedResourceList<SystemDocumentsAttributes>[],
  FAQ: new Resource<SystemDocumentsAttributes>(),
  trademarks: new Resource<SystemDocumentsAttributes>(),
};

const requestPrivacyPolicy = createAction<GenericActionPayload>('systemDocs/requestPrivacyPolicy');
const requestEULA = createAction<GenericActionPayload>('systemDocs/requestEULA');
const requestTermsOfUse = createAction<GenericActionPayload>('systemDocs/requestTermsOfUse');
const requestReleaseNotes = createAction<GenericActionPayload>('systemDocs/requestReleaseNotes');
const requestFAQ = createAction<GenericActionPayload>('systemDocs/requestFAQ');
const requestTrademarks = createAction<GenericActionPayload>('systemDocs/requestTrademarks');

const systemDocs = createSlice({
  name: 'systemDocs',
  initialState,
  reducers: {
    setPrivacyPolicy: (state, action: PayloadAction<Resource<SystemDocumentsAttributes>>) => {
      state.privacyPolicy = action.payload;
    },
    setEULA: (state, action: PayloadAction<Resource<SystemDocumentsAttributes>>) => {
      state.EULA = action.payload;
    },
    setTermsOfUse: (state, action: PayloadAction<Resource<SystemDocumentsAttributes>>) => {
      state.termsOfUse = action.payload;
    },
    setReleaseNotes: (state, action: PayloadAction<UnwrappedResourceList<SystemDocumentsAttributes>[]>) => {
      state.releaseNotes = action.payload;
    },
    setFAQ: (state, action: PayloadAction<Resource<SystemDocumentsAttributes>>) => {
      state.FAQ = action.payload;
    },
    setTrademarks: (state, action: PayloadAction<Resource<SystemDocumentsAttributes>>) => {
      state.trademarks = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStore.type, () => initialState);
  },
});

const {
  setEULA,
  setFAQ,
  setPrivacyPolicy,
  setReleaseNotes,
  setTermsOfUse,
  setTrademarks,
} = systemDocs.actions;

export {
  // Reducer Actions
  setEULA,
  setFAQ,
  setPrivacyPolicy,
  setReleaseNotes,
  setTermsOfUse,
  setTrademarks,

  // Saga Actions
  requestPrivacyPolicy,
  requestEULA,
  requestFAQ,
  requestReleaseNotes,
  requestTermsOfUse,
  requestTrademarks,
};

export const getPrivacyPolicy = (state: RootState) => state.systemDocs.privacyPolicy;
export const getEULA = (state: RootState) => state.systemDocs.EULA;
export const getReleaseNotes = (state: RootState) => state.systemDocs.releaseNotes;
export const getFAQ = (state: RootState) => state.systemDocs.FAQ;
export const getTrademarks = (state: RootState) => state.systemDocs.trademarks;
export const getTermsOfUse = (state: RootState) => state.systemDocs.termsOfUse;

export default systemDocs.reducer;
